import * as React from "react";
import { useEffect, useState } from "react";
import AssessmentResultHeader from "./assessment-result/AssessmentResultHeader";
import AssessmentResultObservations from "./assessment-result/AssessmentResultObservations";
import AssessmentResultTreatmentOptions from "./assessment-result/AssessmentResultTreatmentOptions";
import Report, { assembleReport, ReportModel } from "./commons/model/report/Report";
import { LOCAL_STORAGE_PRINTING_MODEL_KEY } from "./AssessmentResult";
import { PrintableScanPackageViewer } from "./PrintableScanPackageViewer";
import Scan, {
  MouthDescriptor,
  SmilePredictionDescriptors,
  SmileWithApplianceDescriptors,
} from "./commons/model/Scan";
import { isScanEnabled } from "./settings/SmileSimulationTreatmentSettings";
import LoadingSpinner from "./flow/utils/LoadingSpinner";
import FlowType from "./commons/model/FlowType";
import { createRoot } from "react-dom/client";

export default function PrintableAssessmentResult() {
  const [completedPackageViewers, setCompletedPackageViewers] = useState(0);

  const [report, setReport] = useState<Report>(null);

  const [facePhotos, setFacePhotos] = useState<Scan[]>(null);

  const [expectedCompletedPackageViewers, setExpectedCompletedPackageViewers] = useState(0);

  const [model, setModel] = useState<ReportModel>(null);

  useEffect(() => {
    const model = JSON.parse(localStorage.getItem(LOCAL_STORAGE_PRINTING_MODEL_KEY));
    setModel(model);

    localStorage.removeItem(LOCAL_STORAGE_PRINTING_MODEL_KEY);

    const assembledReport = assembleReport(model);
    setReport(assembledReport);

    const filteredFacePhotos = assembledReport.scanPackages.face.filter((scan) =>
      model.smileSimulation.enabled
        ? isScanEnabled(scan.descriptor, model.smileSimulation.settings)
        : !SmileWithApplianceDescriptors.concat(SmilePredictionDescriptors).includes(
            scan.descriptor
          )
    );
    setFacePhotos(filteredFacePhotos);

    setExpectedCompletedPackageViewers(
      2 +
        (!!assembledReport.scanPackages.slightlyOpened.length ? 1 : 0) +
        (!!filteredFacePhotos.length ? 1 : 0)
    );
  }, []);

  const completePackageViewer = () => {
    setCompletedPackageViewers((counter) => counter + 1);
  };

  const descriptors = [
    MouthDescriptor.FACE_SMILING,
    MouthDescriptor.FACE_MOUTH_SLIGHTLY_OPENED,
    MouthDescriptor.CLOSED_FRONT,
    MouthDescriptor.CLOSED_RIGHT,
    MouthDescriptor.CLOSED_LEFT,
    MouthDescriptor.OPENED_TOP,
    MouthDescriptor.OPENED_BOTTOM,
    MouthDescriptor.SLIGHTLY_OPENED_FRONT,
    MouthDescriptor.SLIGHTLY_OPENED_FRONT_RIGHT,
    MouthDescriptor.SLIGHTLY_OPENED_FRONT_LEFT,
    MouthDescriptor.SLIGHTLY_OPENED_RIGHT,
    MouthDescriptor.SLIGHTLY_OPENED_LEFT,
  ];

  const orderByDescriptor = Scan.orderByDescriptor(descriptors);

  useEffect(() => {
    if (
      (model && model.reportType == FlowType.SMILE_SIMULATION) ||
      (completedPackageViewers !== 0 &&
        expectedCompletedPackageViewers !== 0 &&
        completedPackageViewers === expectedCompletedPackageViewers)
    ) {
      setTimeout(() => {
        window.addEventListener("afterprint", () => window.close());
        window.print();
      }, 500);
    }
  }, [completedPackageViewers, expectedCompletedPackageViewers]);

  if (report === null || facePhotos === null) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <AssessmentResultHeader report={report} actions={[]} printMode={true} />
      {model.reportType != FlowType.SMILE_SIMULATION && (
        <div className="break-after">
          <AssessmentResultObservations
            report={report}
            printMode={true}
            criterionUpdatable={false}
          />
        </div>
      )}
      {model.reportType != FlowType.SMILE_SIMULATION && (
        <div className="break-after">
          <AssessmentResultTreatmentOptions report={report} printMode={true} />
        </div>
      )}
      {model.reportType != FlowType.SMILE_SIMULATION && (
        <div className="break-after">
          <h1 className="uppercase">{i18n["Page.AssessmentResult.Photos"]}</h1>
          <PrintableScanPackageViewer
            scans={report.scanPackages.closed.sort(orderByDescriptor)}
            name={i18n["ScanPackage.Closed"]}
            onComplete={completePackageViewer}
          />
          <PrintableScanPackageViewer
            scans={report.scanPackages.opened.sort(orderByDescriptor)}
            name={i18n["ScanPackage.Opened"]}
            onComplete={completePackageViewer}
          />
        </div>
      )}
      {!!report.scanPackages.slightlyOpened.length && (
        <div className="break-after">
          <h1 className="uppercase">{i18n["Page.AssessmentResult.Photos"]}</h1>
          <PrintableScanPackageViewer
            scans={report.scanPackages.slightlyOpened.sort(orderByDescriptor)}
            name={i18n["ScanPackage.SlightlyOpened"]}
            onComplete={completePackageViewer}
          />
        </div>
      )}
      {!!facePhotos.length && (
        <div className="break-after">
          <h1 className="uppercase">{i18n["Page.AssessmentResult.Photos"]}</h1>
          <PrintableScanPackageViewer
            scans={facePhotos.sort(orderByDescriptor)}
            name={i18n["ScanPackage.Face"]}
            onComplete={completePackageViewer}
          />
        </div>
      )}
    </>
  );
}

PrintableAssessmentResult.loadComponent = (elementId: string, props) => {
  const container = document.getElementById(elementId);
  const root = createRoot(container!);
  root.render(<PrintableAssessmentResult {...props} />);
};
